import React, { useState, useMemo, useRef, useEffect } from "react";
import { formatUSD } from "@/utils/formatters";

// =============================================================================
// COHORT REVENUE STACK — HTML Table for Forecast Tab
// =============================================================================
// PURPOSE: Shows how much cash each monthly cohort has generated over time.
// Rows = monthly cohorts (entry month). Cols = months since close.
// Values = cash collected in each window. Color-coded by intensity.
//
// DATA: Passed from parent as pre-computed table data (from RPC or
//       client-side aggregation).
//
// DESIGN: HTML table styled like StaggerChart. No chart library.
//         `?` tooltip with ELI5 explanation.
// =============================================================================

export interface CohortRow {
  cohort_month: string; // e.g. "2025-11"
  // cash by months-since-close: month_0 = close month, month_1 = +1, etc.
  months: Record<number, number>;
}

interface CohortRevenueStackProps {
  data: CohortRow[];
  maxMonthsOut?: number;
}

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatCohortLabel(isoMonth: string): string {
  const [year, month] = isoMonth.split("-").map(Number);
  return `${MONTH_LABELS[month - 1]} '${String(year).slice(2)}`;
}

// Heat color: higher cash = warmer
function cellColor(value: number, max: number): string {
  if (value <= 0 || max <= 0) return "transparent";
  const intensity = Math.min(value / max, 1);
  if (intensity > 0.7) return "rgba(34, 197, 94, 0.25)"; // green
  if (intensity > 0.4) return "rgba(34, 197, 94, 0.15)";
  if (intensity > 0.1) return "rgba(34, 197, 94, 0.08)";
  return "color-mix(in srgb, var(--text-tertiary) 4%, transparent)";
}

function InfoTooltip({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("keydown", handleEsc);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="liquid-glass rounded-lg"
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        marginTop: 8,
        padding: "16px 20px",
        zIndex: 20,
        animation: "fadeIn 0.15s ease-out",
      }}
    >
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-glass-muted hover:text-glass text-xs cursor-pointer"
        style={{ background: "none", border: "none" }}
      >
        ✕
      </button>
      <p className="token-13 text-glass font-medium mb-2">How to read this table</p>
      <p className="token-11 text-glass-secondary leading-relaxed">
        Each row is a group of buyers who signed in the same month.
        <strong className="text-glass"> Close Month</strong> = what they paid upfront when they
        signed.
        <strong className="text-glass"> Month 1, 2, 3...</strong> = installment payments collected
        each month after. The further right, the more of their payment plan has been collected.
      </p>
      <p className="token-11 text-glass-secondary mt-2 leading-relaxed">
        <strong className="text-glass">Why it matters:</strong> Shows whether buyers actually keep
        paying. A strong row = reliable cohort. A row that drops off = people defaulting on
        payments.
      </p>
      <p className="token-11 text-glass-muted mt-2 leading-relaxed">
        Darker green = more cash. "—" = future months (hasn't happened yet).
      </p>
    </div>
  );
}

const CohortRevenueStack: React.FC<CohortRevenueStackProps> = ({ data, maxMonthsOut = 5 }) => {
  const [showInfo, setShowInfo] = useState(false);

  const { maxVal, colHeaders } = useMemo(() => {
    let max = 0;
    for (const row of data) {
      for (const val of Object.values(row.months)) {
        if (val > max) max = val;
      }
    }
    const cols = Array.from({ length: maxMonthsOut + 1 }, (_, i) =>
      i === 0 ? "Close Month" : `Month ${i}`
    );
    return { maxVal: max, colHeaders: cols };
  }, [data, maxMonthsOut]);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-glass-muted text-sm">
        Not enough cohort data yet
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {/* Header — outside liquid-glass so tooltip isn't clipped by overflow:hidden */}
      <div className="flex items-center gap-2 mb-3">
        <h4
          className="font-semibold uppercase"
          style={{ fontSize: 11, color: "var(--text-secondary)", letterSpacing: "0.08em" }}
        >
          Cohort Revenue Stack
        </h4>
        <button
          onClick={() => setShowInfo(!showInfo)}
          className="rounded-full transition-colors"
          style={{
            width: 18,
            height: 18,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 600,
            color: showInfo ? "var(--accent)" : "var(--text-tertiary)",
            border: `1px solid ${showInfo ? "var(--accent)" : "var(--border)"}`,
            background: showInfo
              ? "color-mix(in srgb, var(--accent) 8%, transparent)"
              : "transparent",
            cursor: "pointer",
          }}
        >
          ?
        </button>
      </div>

      {showInfo && <InfoTooltip onClose={() => setShowInfo(false)} />}

      <div className="liquid-glass anim-lift p-4">
        {/* Table */}
        <div className="overflow-x-auto">
          <table
            className="w-full token-11"
            style={{ borderCollapse: "separate", borderSpacing: 2 }}
          >
            <thead>
              <tr>
                <th className="text-left text-glass-muted font-semibold px-2 py-1.5">Cohort</th>
                {colHeaders.map((label, i) => (
                  <th key={i} className="text-right text-glass-muted font-medium px-2 py-1.5">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr
                  key={row.cohort_month}
                  className="anim-pop"
                  style={{
                    animationDelay: `${Math.min(idx, 15) * 100}ms`,
                    animationDuration: "480ms",
                  }}
                >
                  <td className="text-glass-secondary font-medium px-2 py-1.5 whitespace-nowrap">
                    {formatCohortLabel(row.cohort_month)}
                  </td>
                  {Array.from({ length: maxMonthsOut + 1 }, (_, i) => {
                    const val = row.months[i];
                    const hasValue = val !== undefined && val > 0;
                    return (
                      <td
                        key={i}
                        className="text-right px-2 py-1.5 rounded"
                        style={{
                          background: hasValue ? cellColor(val, maxVal) : "transparent",
                          color: hasValue ? "var(--text-primary)" : "var(--text-tertiary)",
                        }}
                      >
                        {hasValue ? formatUSD(val).replace(".00", "") : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CohortRevenueStack;
